import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Eye, Cpu, Brain, Target, Building2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

// Parsed from Ad_Targeting.csv
const algorithmData = {
  profile: {
    age: "18-24",
    experience: "8 years",
    gender: "Male",
    graduationYear: "2021",
    location: "Bengaluru, India",
    locale: "English",
  },
  memberTraits: [
    { trait: "Mac User", icon: "💻", comment: "Guilty as charged" },
    { trait: "Desktop/Laptop User", icon: "🖥️", comment: "Not a mobile-first person apparently" },
    { trait: "Career Changer", icon: "🔄", comment: "Wait, what career did I change from?" },
  ],
  topInterests: [
    "Cloud Computing", "Artificial Intelligence", "Machine Learning", "Web Development",
    "Startups", "Open Source Software", "Distributed Computing", "Quantum Computing",
    "Blockchain Technology", "Robotics", "Natural Language Processing", "Deep Learning",
    "Computer Vision", "Financial Technology", "Venture Capital", "Cybersecurity",
    "Data Science", "Big Data", "Virtual Reality", "Augmented Reality",
  ],
  funnyInterests: [
    { interest: "Swarm Robotics", reaction: "I googled this once" },
    { interest: "Nanotechnology", reaction: "Watched a documentary" },
    { interest: "Drones", reaction: "Don't even own one" },
    { interest: "Wearable Tech", reaction: "Have an Apple Watch I guess?" },
    { interest: "Video Games", reaction: "Finally, something accurate" },
    { interest: "Digital Currency", reaction: "Lost money in 2022 like everyone else" },
    { interest: "Chatbots", reaction: "Well, I do build AI things..." },
    { interest: "Quantum Computing", reaction: "Read one blog post" },
  ],
  topSkills: [
    "JavaScript", "TypeScript", "React.js", "Node.js", "Python", "Java", "Kotlin",
    "System Architecture", "Distributed Systems", "Machine Learning", "AI Agents",
    "Cloud Computing", "Docker", "Kubernetes", "GraphQL", "REST APIs",
  ],
  funnySkills: [
    { skill: "Swarm Intelligence", reaction: "My college project haunts me" },
    { skill: "Genetic Algorithms", reaction: "One assignment, forever tagged" },
    { skill: "Healing", reaction: "????" },
    { skill: "Dance Music", reaction: "I listen to it, doesn't mean I'm skilled" },
    { skill: "Jewelry Sales", reaction: "LinkedIn, we need to talk" },
    { skill: "Egyptian Arabic", reaction: "Absolutely not" },
    { skill: "Cabinet Vision", reaction: "What even is this?" },
    { skill: "Survival Skills", reaction: "Surviving deadlines maybe" },
    { skill: "Fiber Arts", reaction: "I don't knit, LinkedIn" },
    { skill: "Menu Engineering", reaction: "I order food, not engineer menus" },
  ],
  companies: [
    "OpenAI", "Meta", "Google", "Apple", "Netflix", "Amazon", "Microsoft",
    "Anthropic", "Stripe", "Figma", "Notion", "Linear", "Vercel", "GitHub",
  ],
  funnyCompanies: [
    { company: "Tinder", reaction: "Research purposes only" },
    { company: "Decathlon", reaction: "Bought a yoga mat once" },
    { company: "Trek Bicycle", reaction: "Don't even cycle" },
    { company: "DHL Global Forwarding", reaction: "Tracking a package?" },
    { company: "Delta Air Lines", reaction: "Flew with them twice" },
    { company: "Macy's", reaction: "LinkedIn, I'm in India" },
  ],
  groups: [
    "ICPC - International Collegiate Programming Contest",
    "Google Summer of Code 2020/2021 Alumni",
    "India Young Professionals",
    "Technology Investor Club: AI, Data Science, Fintech, IoT, Robotics",
  ],
};

const AlgorithmView = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('profile');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [glitchText, setGlitchText] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitchText(true);
      setTimeout(() => setGlitchText(false), 150);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const sections = [
    { id: 'profile', label: 'Profile', icon: Eye },
    { id: 'traits', label: 'Traits', icon: Brain },
    { id: 'interests', label: 'Interests', icon: Sparkles },
    { id: 'skills', label: 'Skills', icon: Cpu },
    { id: 'companies', label: 'Companies', icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-green-500/30">
      {/* Scanline effect */}
      <div
        className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.1) 2px, rgba(0,255,0,0.1) 4px)',
        }}
      />

      {/* Grid background */}
      <div
        className="fixed inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,255,0,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,255,0,0.3) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Navigation */}
      <div className="relative z-10 flex items-center justify-between px-8 py-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="gap-2 text-green-500/70 hover:text-green-400 hover:bg-green-500/10"
          >
            <ArrowLeft className="w-4 h-4" />
            Exit Matrix
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2 text-green-500/50 text-xs font-mono"
        >
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          LIVE DATA FEED
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <p className="text-green-500/60 text-sm font-mono mb-4 tracking-widest">
            // CLASSIFIED DATA EXTRACT
          </p>
          <h1 className={`text-5xl md:text-7xl font-black mb-6 transition-all ${glitchText ? 'text-red-500 translate-x-1' : 'text-white'}`}>
            What LinkedIn
            <br />
            <span className="text-green-400">Thinks I Am</span>
          </h1>
          <p className="text-white/40 text-lg max-w-2xl mx-auto font-mono">
            LinkedIn's ad targeting algorithm has built a profile of me based on my activity.
            Some of it is accurate. Most of it is... interesting.
          </p>
        </motion.div>

        {/* Section Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-sm transition-all ${
                activeSection === section.id
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'text-white/40 hover:text-white/70 border border-transparent hover:border-white/10'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </motion.div>

        {/* Content Sections */}
        <AnimatePresence mode="wait">
          {activeSection === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-8">
                <h2 className="text-green-400 font-mono text-sm mb-6 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  BASIC PROFILE DATA
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {Object.entries(algorithmData.profile).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <p className="text-white/30 text-xs font-mono uppercase">{key.replace(/([A-Z])/g, ' $1')}</p>
                      <p className="text-white text-lg font-mono">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-8">
                <h2 className="text-white/70 font-mono text-sm mb-6">MEMBER GROUPS</h2>
                <div className="space-y-3">
                  {algorithmData.groups.map((group, i) => (
                    <div key={i} className="flex items-center gap-3 text-white/60">
                      <span className="text-green-500 font-mono text-xs">[{String(i + 1).padStart(2, '0')}]</span>
                      <span className="text-sm">{group}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeSection === 'traits' && (
            <motion.div
              key="traits"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-8">
                <h2 className="text-green-400 font-mono text-sm mb-8 flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  MEMBER TRAITS DETECTED
                </h2>
                <div className="space-y-6">
                  {algorithmData.memberTraits.map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-black/30 rounded-lg border border-white/5">
                      <span className="text-3xl">{item.icon}</span>
                      <div>
                        <p className="text-white font-medium text-lg">{item.trait}</p>
                        <p className="text-green-400/70 text-sm font-mono mt-1">// {item.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6">
                <p className="text-amber-400/80 text-sm font-mono">
                  ⚠️ Note: LinkedIn marked me as a "Career Changer" but I've been in tech since 2017.
                  Maybe they know something I don't?
                </p>
              </div>
            </motion.div>
          )}

          {activeSection === 'interests' && (
            <motion.div
              key="interests"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-8">
                <h2 className="text-green-400 font-mono text-sm mb-6">INTERESTS - ACTUALLY ACCURATE</h2>
                <div className="flex flex-wrap gap-2">
                  {algorithmData.topInterests.map((interest, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm font-mono"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-8">
                <h2 className="text-red-400 font-mono text-sm mb-6">INTERESTS - QUESTIONABLE</h2>
                <div className="space-y-4">
                  {algorithmData.funnyInterests.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5 group hover:border-red-500/30 transition-colors"
                    >
                      <span className="text-white/70">{item.interest}</span>
                      <span className="text-red-400/70 text-sm font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                        // {item.reaction}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeSection === 'skills' && (
            <motion.div
              key="skills"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-8">
                <h2 className="text-green-400 font-mono text-sm mb-6">SKILLS - YES, I KNOW THESE</h2>
                <div className="flex flex-wrap gap-2">
                  {algorithmData.topSkills.map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm font-mono"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-8">
                <h2 className="text-red-400 font-mono text-sm mb-6">SKILLS - LINKEDIN IS HIGH</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {algorithmData.funnySkills.map((item, i) => (
                    <div
                      key={i}
                      className="p-4 bg-black/30 rounded-lg border border-white/5 hover:border-red-500/30 transition-colors"
                    >
                      <p className="text-white/80 font-medium">{item.skill}</p>
                      <p className="text-red-400/60 text-sm font-mono mt-1">// {item.reaction}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center text-white/30 text-sm font-mono">
                LinkedIn thinks I have 500+ skills. I counted. It's absurd.
              </div>
            </motion.div>
          )}

          {activeSection === 'companies' && (
            <motion.div
              key="companies"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-8">
                <h2 className="text-green-400 font-mono text-sm mb-6">COMPANIES I FOLLOW - MAKES SENSE</h2>
                <div className="flex flex-wrap gap-2">
                  {algorithmData.companies.map((company, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm font-mono"
                    >
                      {company}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-8">
                <h2 className="text-amber-400 font-mono text-sm mb-6">COMPANIES I FOLLOW - WHY THO</h2>
                <div className="space-y-4">
                  {algorithmData.funnyCompanies.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5 hover:border-amber-500/30 transition-colors"
                    >
                      <span className="text-white/70">{item.company}</span>
                      <span className="text-amber-400/70 text-sm font-mono">// {item.reaction}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center text-white/30 text-sm font-mono">
                I follow 400+ companies. LinkedIn remembers every click.
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-20 text-center space-y-4"
        >
          <p className="text-white/20 text-xs font-mono">
            Data extracted from LinkedIn Ad Targeting export (Dec 2025)
          </p>
          <p className="text-green-500/40 text-xs font-mono">
            The algorithm sees what it wants to see. I'm just here for the ride.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AlgorithmView;
